
import {
  updateDoc,
  doc,
  getDoc,
  getFirestore,
  deleteField,
} from 'firebase/firestore';

import { getApp } from 'firebase/app';
import type { State, TableSortPreference, TableSortPreferences } from './types';

function normalizeTableSorting(value: unknown): TableSortPreferences {
  if (value == null || typeof value !== 'object' || Array.isArray(value)) {
    return {};
  }

  return Object.entries(value as Record<string, unknown>).reduce<TableSortPreferences>((acc, [tableKey, sortValue]) => {
    if (sortValue == null) {
      acc[tableKey] = null;
      return acc;
    }

    if (typeof sortValue !== 'object' || Array.isArray(sortValue)) {
      return acc;
    }

    const sort = sortValue as Partial<TableSortPreference>;
    if (
      typeof sort.columnKey === 'string'
      && (sort.order === 'ascend' || sort.order === 'descend')
    ) {
      acc[tableKey] = {
        columnKey: sort.columnKey,
        order: sort.order,
      };
    }

    return acc;
  }, {});
}

export async function changeDisplayPreferences(displayPreferences: State['userPreferences']['displayMode']) {
  const fbApp = getApp(this.firebaseApp);
  const db = getFirestore(fbApp, this.dbid);

  await updateDoc(doc(db, 'users', this.user.uid), {
    'preferences.displayMode': displayPreferences,
    'displayPreferences': deleteField(),
  });

  this.userPreferences.displayMode = displayPreferences;
}

export async function changeDarkMode(darkMode: boolean) {
  const fbApp = getApp(this.firebaseApp);
  const db = getFirestore(fbApp, this.dbid);

  await updateDoc(doc(db, 'users', this.user.uid), {
    'preferences.darkMode': darkMode,
    'darkMode': deleteField(),
  });
  this.userPreferences.darkMode = darkMode;
}

export async function changeNumbering(numbering: boolean) {
  const fbApp = getApp(this.firebaseApp);
  const db = getFirestore(fbApp, this.dbid);

  await updateDoc(doc(db, 'users', this.user.uid), {
    'preferences.numbering': numbering,
    'numbering': deleteField(),
  });
  this.userPreferences.numbering = numbering;
}

export async function changeNumberingDirection(numberingDirection: State['userPreferences']['numberingDirection']) {
  const fbApp = getApp(this.firebaseApp);
  const db = getFirestore(fbApp, this.dbid);

  await updateDoc(doc(db, 'users', this.user.uid), {
    'preferences.numberingDirection': numberingDirection,
    'numberingDirection': deleteField(),
  });
  this.userPreferences.numberingDirection = numberingDirection;
}

export async function changeNameOverlay(nameOverlay: number) {
  const fbApp = getApp(this.firebaseApp);
  const db = getFirestore(fbApp, this.dbid);

  await updateDoc(doc(db, 'users', this.user.uid), {
    'preferences.nameOverlay': nameOverlay,
  });
  this.userPreferences.nameOverlay = nameOverlay;
}

export async function changeAutosave(autosave: boolean) {
  const fbApp = getApp(this.firebaseApp);
  const db = getFirestore(fbApp, this.dbid);

  await updateDoc(doc(db, 'users', this.user.uid), {
    'preferences.autosave': autosave,
  });
  this.userPreferences.autosave = autosave;
}

export async function changeUnitSystem(unitSystem: State['userPreferences']['unitSystem']) {
  const fbApp = getApp(this.firebaseApp);
  const db = getFirestore(fbApp, this.dbid);

  await updateDoc(doc(db, 'users', this.user.uid), {
    'preferences.unitSystem': unitSystem,
  });
  this.userPreferences.unitSystem = unitSystem;
}

export async function changeTableSortPreference(tableKey: string, sortPreference: TableSortPreference | null) {
  const fbApp = getApp(this.firebaseApp);
  const db = getFirestore(fbApp, this.dbid);
  const tableSorting = {
    ...(this.userPreferences.tableSorting || {}),
    [tableKey]: sortPreference,
  };

  await updateDoc(doc(db, 'users', this.user.uid), {
    'preferences.tableSorting': tableSorting,
  });

  this.userPreferences.tableSorting = tableSorting;
}

/** Persist that the team switcher tour was shown (syncs across devices). */
export async function setTeamSwitcherTourShown() {
  const fbApp = getApp(this.firebaseApp);
  const db = getFirestore(fbApp, this.dbid);

  await updateDoc(doc(db, 'users', this.user.uid), {
    'preferences.teamSwitcherTourShown': true,
  });
}

export async function getUserPreferences(uid: string) {
  const fbApp = getApp(this.firebaseApp);
  const db = getFirestore(fbApp, this.dbid);

  // Get user creation date to determine default autosave value
  // Accounts created today or later default to on, older accounts default to off
  const user = this.user;
  const today = new Date('2026-01-10');
  today.setHours(0, 0, 0, 0);
  let defaultAutosave = false;

  if (user?.createdAt) {
    try {
      const userCreationDate = new Date(parseInt(user.createdAt));
      userCreationDate.setHours(0, 0, 0, 0);
      defaultAutosave = userCreationDate >= today;
    } catch (error) {
      console.error('Error parsing user creation date:', error);
    }
  }

  const pref: State['userPreferences'] = {
    darkMode: false,
    displayMode: 'table',
    numbering: false,
    numberingDirection: 'DESC',
    nameOverlay: 2,
    autosave: defaultAutosave,
    unitSystem: 'imperial',
    tableSorting: {},
  };

  const docRef = doc(db, 'users', uid);
  const docSnap = await getDoc(docRef);

  if (docSnap.exists()) {
    const userData = docSnap.data();

    // For old preferences format, check both root level and preferences level
    if (userData?.darkMode == null && userData?.preferences?.darkMode == null) {
      pref.darkMode = false;
    } else {
      pref.darkMode = docSnap.data()?.preferences?.darkMode == null
        ? docSnap.data().darkMode
        : docSnap.data().preferences.darkMode;
    }

    if (userData?.displayPreferences == null && userData?.preferences?.displayMode == null) {
      pref.displayMode = 'table';
    } else {
      pref.displayMode = docSnap.data()?.preferences?.displayMode == null
        ? docSnap.data().displayPreferences
        : docSnap.data().preferences.displayMode;
    }

    if (userData?.numbering == null && userData?.preferences?.numbering == null) {
      pref.numbering = false;
    } else {
      pref.numbering = docSnap.data()?.preferences?.numbering == null
        ? docSnap.data().numbering
        : docSnap.data().preferences.numbering;
    }

    if (userData?.numberingDirection == null && userData?.preferences?.numberingDirection == null) {
      pref.numberingDirection = 'DESC';
    } else {
      pref.numberingDirection = docSnap.data()?.preferences?.numberingDirection == null
        ? docSnap.data().numberingDirection
        : docSnap.data().preferences.numberingDirection;
    }

    // New preferences only stored in preferences level
    if (userData?.preferences?.nameOverlay == null) {
      pref.nameOverlay = 2;
    } else {
      pref.nameOverlay = docSnap.data().preferences.nameOverlay;
    }

    // Autosave preference: if it doesn't exist in store, use cohort-based default
    if (userData?.preferences?.autosave == null) {
      pref.autosave = defaultAutosave;
    } else {
      pref.autosave = docSnap.data().preferences.autosave;
    }

    if (userData?.preferences?.unitSystem == null) {
      pref.unitSystem = 'imperial';
    } else {
      pref.unitSystem = docSnap.data().preferences.unitSystem;
    }

    pref.tableSorting = normalizeTableSorting(userData?.preferences?.tableSorting);
  }

  this.userPreferences = pref;
}
